import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../lib/utils'
import './tabs.scss'

const tabsListVariants = cva(
  'inline-flex items-center justify-start gap-0 border-border p-0 text-muted-foreground',
  {
    variants: {
      variant: {
        simple: 'w-full border-b-[1px]',
        default: 'h-10 justify-center rounded-md bg-muted p-1 w-full',
        // Rounded pill "track" holding pill buttons — used for tabs nested one
        // level inside another tab (e.g. Team setup's Users/Teams), so they
        // read as visually distinct from the top-level `simple` underline tabs.
        segmented: 'w-auto gap-0.5 rounded-full border bg-muted p-[3px]',
      },
    },
    defaultVariants: {
      variant: 'simple',
    },
  },
)

const tabsTriggerVariants = cva(
  'inline-flex items-center justify-start whitespace-nowrap transition-all focus-visible:outline-none disabled:pointer-events-none',
  {
    variants: {
      variant: {
        simple:
          'mx-2 -mb-[2px] border-b-2 px-2 py-2 font-inter text-s-500 tracking-normal normal-case ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 first-of-type:ml-0 data-[state=active]:border-primary data-[state=inactive]:border-transparent data-[state=active]:text-foreground',
        default:
          'rounded-md border border-transparent px-2 py-1.5 text-xs-500 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:border-border data-[state=active]:text-foreground',
        // Raised pill when active (bg-card + border + shadow over the bg-muted
        // track), muted text when inactive — pair with TabsList variant="segmented".
        segmented:
          'justify-center rounded-full border px-4 py-1.5 text-s-500 text-tertiary ring-offset-background transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=inactive]:hover:text-secondary data-[state=active]:border-border data-[state=inactive]:border-transparent data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      },
    },
    defaultVariants: {
      variant: 'simple',
    },
  },
)

const tabsContentVariants = cva(
  'ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-0',
)

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant }), className)}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & { forceMount?: boolean }
>(({ className, forceMount, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(tabsContentVariants(), forceMount && 'data-[state=inactive]:hidden', className)}
    {...(forceMount && { forceMount })}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
